import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  AxiosRequestConfig,
} from "axios";
import { getLocaleWOWNSelected, getTokenFromStorage } from "./utils";
import { getPublicEnv } from '@/utils/env';

export enum EHttpStatusCode {
  Success = 200,
  NotFound = 404,
  ServerError = 500,
  Forbidden = 403,
  Unauthorized = 401,
}

interface IApiConfig {
  baseURL: string;
}

interface IErrorResponse {
  message?: string | string[];
  error?: string;
  statusCode?: EHttpStatusCode;
}

export interface IHttpError {
  message?: string | string[];
  statusCode?: EHttpStatusCode;
}

const apiConfig: IApiConfig = {
  baseURL: getPublicEnv('NEXT_PUBLIC_API_URL') || "",
};

const axiosInstance: AxiosInstance = axios.create(apiConfig);

// Request Interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const { token } = getTokenFromStorage();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const locale = getLocaleWOWNSelected();
    if (locale) {
      config.headers["X-Target-Language"] = locale;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Error handling interceptor
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  (axiosError: AxiosError) => {
    const errorResponse: IHttpError = {};

    if (axiosError.response) {
      const { statusCode, message } = axiosError.response
        .data as IErrorResponse;
      // The request was made and the server responded with a status code
      errorResponse.message = message;
      errorResponse.statusCode = statusCode;
      // Initialize token when user is available
      if (errorResponse.statusCode === EHttpStatusCode.Unauthorized) {
        console.error("Unauthorized! Redirecting to login...");
        globalThis.location.href = "/login"; // Redirect to login page on 401
      }
    } else if (axiosError.request) {
      // The request was made but no response was received
      errorResponse.message = "Request Error";
    } else {
      // Something happened in setting up the request
      errorResponse.message = "General Error";
      errorResponse.statusCode = EHttpStatusCode.ServerError;
    }

    console.error(errorResponse);
    let errorMessage: string;
    if (typeof errorResponse.message === "string") {
      errorMessage = errorResponse.message;
    } else if (Array.isArray(errorResponse.message)) {
      errorMessage = errorResponse.message.join(", ");
    } else {
      errorMessage = "Unknown error occurred";
    }
    return Promise.reject(new Error(errorMessage));
  },
);

// API methods
const api = {
  get: <T>(url: string, params = {}): Promise<AxiosResponse<T>> =>
    axiosInstance.get<T>(url, { params }),

  post: <T, D = unknown>(
    url: string,
    data: D,
    config?: AxiosRequestConfig<D>,
  ): Promise<AxiosResponse<T>> => axiosInstance.post<T>(url, data, config),

  put: <T, D = unknown>(
    url: string,
    data: D,
    config?: AxiosRequestConfig<D>,
  ): Promise<AxiosResponse<T>> => axiosInstance.put<T>(url, data, config),

  delete: <T>(
    url: string,
    config?: AxiosRequestConfig | undefined,
  ): Promise<AxiosResponse<T>> => axiosInstance.delete<T>(url, config),
};

export default api;
